/**
 * INV-2: PO Receiving Metadata Display Helpers
 *
 * Centralized formatting + multi-PO aggregation logic for displaying
 * receiving metadata (temperature, handling, case count, putaway, item desc)
 * across execution surfaces.
 */

export interface POMetaFields {
  requiredTemperature?: string | null;
  handlingType?: string | null;
  caseCount?: number | null;
  itemDescription?: string | null;
  putawayZone?: string | null;
}

/**
 * Returns true if the PO has at least one non-empty receiving metadata field.
 */
export function hasPOMetadata(po: POMetaFields): boolean {
  return !!(
    po.requiredTemperature ||
    po.handlingType ||
    (po.caseCount != null && po.caseCount > 0) ||
    po.putawayZone
  );
}

/**
 * Format a single PO's metadata as a dot-separated string.
 *
 * @param po - The PO metadata fields
 * @param opts.compact - If true, excludes putawayZone (for tiles/tooltips)
 * @returns Formatted string like "Frozen · Clamp · 480 cases · Freezer A", or null if no fields
 */
export function formatPOMetadata(
  po: POMetaFields,
  opts?: { compact?: boolean }
): string | null {
  const parts: string[] = [];

  if (po.requiredTemperature) parts.push(po.requiredTemperature);
  if (po.handlingType) parts.push(po.handlingType);
  if (po.caseCount != null && po.caseCount > 0) {
    parts.push(`${po.caseCount} cases`);
  }
  if (!opts?.compact && po.putawayZone) parts.push(po.putawayZone);

  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Aggregate metadata across multiple POs with safety logic.
 *
 * Rules:
 * - Single PO → format normally
 * - Multiple POs with identical temperature & handling → show shared values
 * - Multiple POs with mixed values → "Multiple PO requirements"
 * - Load-linked with identical temp → "Frozen load"
 * - Load-linked with mixed temp → "Mixed requirements"
 * - No metadata on any PO → null (render nothing)
 *
 * @param pos - Array of PO metadata
 * @param opts.compact - Exclude putawayZone
 * @param opts.loadLinked - If true, uses load-specific aggregation ("Frozen load" / "Mixed requirements")
 * @param opts.temperatureOnly - If true, only show temperature + case count (calendar tooltip)
 */
export function aggregatePOMetadata(
  pos: POMetaFields[],
  opts?: { compact?: boolean; loadLinked?: boolean; temperatureOnly?: boolean }
): string | null {
  if (!pos || pos.length === 0) return null;

  // Filter to POs that actually have metadata
  const withMeta = pos.filter(hasPOMetadata);
  if (withMeta.length === 0) return null;

  // Single PO — format directly
  if (pos.length === 1) {
    if (opts?.temperatureOnly) {
      const parts: string[] = [];
      if (pos[0].requiredTemperature) parts.push(pos[0].requiredTemperature);
      if (pos[0].caseCount != null && pos[0].caseCount > 0) {
        parts.push(`${pos[0].caseCount} cases`);
      }
      return parts.length > 0 ? parts.join(' · ') : null;
    }
    return formatPOMetadata(pos[0], opts);
  }

  // Multiple POs — check for identical values
  const temps = [...new Set(pos.map(p => p.requiredTemperature).filter(Boolean))];
  const handlings = [...new Set(pos.map(p => p.handlingType).filter(Boolean))];

  // Load-linked aggregation
  if (opts?.loadLinked) {
    if (temps.length === 1) return `${temps[0]} load`;
    if (temps.length > 1) return 'Mixed requirements';
    return null; // No temperature data on any PO
  }

  // Temperature-only mode (calendar tooltip)
  if (opts?.temperatureOnly) {
    if (temps.length <= 1 && withMeta.length > 0) {
      const parts: string[] = [];
      if (temps[0]) parts.push(temps[0]);
      return parts.length > 0 ? parts.join(' · ') : null;
    }
    return 'Multiple PO requirements';
  }

  // Standard multi-PO aggregation
  if (temps.length <= 1 && handlings.length <= 1) {
    // All POs share same temp + handling — show shared values (no case count to avoid false precision)
    const parts: string[] = [];
    if (temps[0]) parts.push(temps[0]);
    if (handlings[0]) parts.push(handlings[0]);
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  return 'Multiple PO requirements';
}

/**
 * INV-4.1: Extract PO compatibility metadata for the dock scoring engine.
 *
 * Single aggregation path — all callers of getRankedDockAssignments() should
 * use this function instead of inline extraction logic.
 *
 * @returns POMetadataForScoring-compatible object, or undefined if no linked POs
 */
export function extractPOMetadataForScoring(
  linkedPOs: POMetaFields[] | undefined | null
): { requiredTemperature: string | null; handlingType: string | null; isMixed: boolean } | undefined {
  if (!linkedPOs || linkedPOs.length === 0) return undefined;

  const temps = [...new Set(linkedPOs.map(p => p.requiredTemperature).filter(Boolean))];
  const handlings = [...new Set(linkedPOs.map(p => p.handlingType).filter(Boolean))];

  if (temps.length > 1) {
    return { requiredTemperature: null, handlingType: null, isMixed: true };
  }

  return {
    requiredTemperature: temps[0] || null,
    handlingType: handlings.length === 1 ? handlings[0]! : null,
    isMixed: false,
  };
}
